import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Button, DateTimePicker, Modal, ModalCloseButton, ModalDescription, ModalFooter, ModalHeader, NotificationDispatch, type SelectOption, TextField } from '@tryhackme/thm-ui-components';
import { subDays } from 'date-fns';
import { useState } from 'react';
import { useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { SeatType, defaultSeatType } from '../../corporate.constants';
import { useAddSeatMutation } from '../../corporate.slice';
import { StyledAddIcon } from '../../corporate.styles';
import type { AddSeatModalProps, CreateSeatProps } from '../../corporate.types';
import { StyledButton, StyledInfoBox, StyledModalTitle, StyledTextField } from './add-seat-modal.styles';
export const AddSeatModal = ({
  companyDetails
}: AddSeatModalProps) => {
  const [addSeat] = useAddSeatMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>();
  const [selectedOption, setSelectedOption] = useState<SelectOption>({
    id: '',
    value: '',
    label: ''
  });
  const forbidEmailEntry = selectedOption.value === 'azure' && !companyDetails?.additionalContent?.azureTenantLabs?.labsReady;
  const handleModalClose = () => {
    setModalOpen(false);
  };
  const handleSelection = (option: SelectOption = defaultSeatType) => {
    setSelectedOption(() => ({
      id: option.id,
      value: option.value,
      label: option.label
    }));
  };
  const {
    register,
    getValues,
    reset,
    handleSubmit
  } = useForm<CreateSeatProps>({
    defaultValues: {
      companyId: '',
      emails: '',
      blankUsers: '',
      seatType: ''
    },
    mode: 'onChange'
  });
  const onSubmit = async () => {
    try {
      const {
        emails,
        blankUsers
      }: CreateSeatProps = getValues();
      const userEmails: string[] = emails.length > 0 ? emails.split(',').map((email: string) => email.trim()) : [];
      await addSeat({
        companyId: companyDetails._id,
        userEmails,
        blankUsers: blankUsers === '' ? 0 : Number.parseInt(blankUsers, 10),
        seatType: selectedOption.value === '' ? 'regular' : selectedOption.value,
        subDuration: date?.toString() ?? ''
      }).unwrap();
      NotificationDispatch('success', 'Seat created successfully');
    } catch (error) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
    reset();
    handleModalClose();
  };
  return <Modal onOpenChange={open => {
    setModalOpen(open);
    reset();
  }} open={modalOpen} triggerElement={<StyledButton variant="outlined" color="secondary">
          <StyledAddIcon icon={faPlus} /> Add seats
        </StyledButton>} isSmall overflowVisible data-sentry-element="Modal" data-sentry-component="AddSeatModal" data-sentry-source-file="add-seat-modal.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="add-seat-modal.tsx">
        <StyledModalTitle isSmall data-sentry-element="StyledModalTitle" data-sentry-source-file="add-seat-modal.tsx">Add seats</StyledModalTitle>
      </ModalHeader>
      <ModalDescription data-sentry-element="ModalDescription" data-sentry-source-file="add-seat-modal.tsx">Copy & paste all emails in the box below separated by a comma or a space.</ModalDescription>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id="add-users" method="dialog" onSubmit={handleSubmit(onSubmit)}>
        <TextField {...register('emails')} id="email-id" label="Seats' email addresses" placeholder="example1@thm.com, example2@thm.com, example3@thm.com" rows={4} variant="textarea" disabled={forbidEmailEntry} isDisabled={forbidEmailEntry} data-sentry-element="TextField" data-sentry-source-file="add-seat-modal.tsx" />
        {forbidEmailEntry && <StyledInfoBox>
            <p>
              Azure tenant labs are not provisioned for this company, so only blank seats can be added. A request to
              provision Azure tenant labs will be sent when adding blank seats.
            </p>
          </StyledInfoBox>}
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="add-seat-modal.tsx">
          <TextField {...register('blankUsers')} id="blank-users-id" label="Blank users" placeholder="Enter a value" variant="input" type="number" min={0} data-sentry-element="TextField" data-sentry-source-file="add-seat-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="add-seat-modal.tsx">
          <DateTimePicker label="Seat end date *" selected={date} placeholderText="None selected" popperPlacement="bottom" minDate={subDays(new Date(), 30)} timeIntervals={15} isClearable shouldCloseOnSelect onChange={newDate => setDate(newDate)} autoComplete="off" required data-sentry-element="DateTimePicker" data-sentry-source-file="add-seat-modal.tsx" />
        </StyledTextField>
        <StyledTextField data-sentry-element="StyledTextField" data-sentry-source-file="add-seat-modal.tsx">
          <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleSelection} options={SeatType} label="Seat type" selected={selectedOption.value === '' ? SeatType[0] : selectedOption} defaultValue={SeatType[0]} placeholder="Seat type" data-sentry-element="TextField" data-sentry-source-file="add-seat-modal.tsx" />
        </StyledTextField>

        <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="add-seat-modal.tsx">
          <ModalCloseButton text="Cancel" handleCloseButtonAction={() => reset()} data-sentry-element="ModalCloseButton" data-sentry-source-file="add-seat-modal.tsx" />
          <Button type="submit" color="primary" variant="solid" data-sentry-element="Button" data-sentry-source-file="add-seat-modal.tsx">
            Add seats
          </Button>
        </ModalFooter>
      </form>
    </Modal>;
};